import { DOCUMENT, NgLocaleLocalization } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { clearTranslations, loadTranslations, MessageId, TargetMessage } from '@angular/localize';
import { isNil } from '@trimble-gcs/common';
import {
  getLocaleInfo,
  LocaleId,
  NG_DEFAULT_LOCALE,
  PluralArgs,
  PluralMap,
  SelectArgs,
  SelectMap,
  TranslationUnit,
} from '@trimble-gcs/i18n-types';
import { getLangDir } from 'rtl-detect';
import { LocaleLoader } from './locale-loader';
import { TranslationLoader } from './translation-loader';

const INTERPOLATION_REGEXP = /\{([\w ]+)\}/g;

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  private localeLoader = inject(LocaleLoader);
  private translationLoader = inject(TranslationLoader);
  private document = inject(DOCUMENT);

  private _currentLocale: LocaleId = NG_DEFAULT_LOCALE;

  get currentLocale(): LocaleId {
    return this._currentLocale ?? NG_DEFAULT_LOCALE;
  }

  async setLocale(locale: string): Promise<void> {
    try {
      const { localeId, ngLocale } = getLocaleInfo(locale);

      if (localeId === NG_DEFAULT_LOCALE) {
        return this.useDefaultLocale();
      }

      await this.localeLoader.loadLocale(ngLocale, localeId);
      const translations = await this.translationLoader.fetchTranslations(localeId);

      if (translations && Object.keys(translations).length > 0) {
        this.loadTranslations(localeId, translations);
      } else {
        this.useDefaultLocale();
      }
    } catch {
      return this.useDefaultLocale();
    }
  }

  translate(text: string, args: Record<string, unknown>): string {
    if (!text || !args) return text;
    return text.replace(INTERPOLATION_REGEXP, (_, key) =>
      key in args ? String(args[key]) : `{${key}}`,
    );
  }

  translatePlural(pluralArgs: PluralArgs, pluralMap: PluralMap, locale?: string): string {
    if (isNil(pluralArgs)) throw new Error("Argument 'pluralArgs' is required.");
    if (isNil(pluralMap)) throw new Error("Argument 'pluralMap' is required.");

    const { count } = pluralArgs;
    const translationUnit = this.getPluralTranslationUnit(count, pluralMap, locale);

    if (isNil(translationUnit))
      throw new Error(`No TranslationUnit found for plural key: ${count}`);

    return translationUnit.text.replace(INTERPOLATION_REGEXP, (match, key) =>
      key in pluralArgs ? String(pluralArgs[key]) : match,
    );
  }

  translateSelect(selectArgs: SelectArgs, selectMap: SelectMap): string {
    if (isNil(selectArgs)) throw new Error("Argument 'selectArgs' is required.");
    if (isNil(selectMap)) throw new Error("Argument 'selectMap' is required.");

    const { select } = selectArgs;
    const translationUnit = selectMap[select];

    if (isNil(translationUnit)) throw new Error(`No TranslationUnit found for select: ${select}`);

    return translationUnit.text.replace(INTERPOLATION_REGEXP, (match, key) =>
      key in selectArgs ? String(selectArgs[key]) : match,
    );
  }

  private useDefaultLocale(): void {
    clearTranslations();
    this.setDocumentI18n(NG_DEFAULT_LOCALE);
    this._currentLocale = NG_DEFAULT_LOCALE;
  }

  private loadTranslations(
    localeId: LocaleId,
    translations: Record<MessageId, TargetMessage>,
  ): void {
    clearTranslations();
    loadTranslations(translations);
    this.setDocumentI18n(localeId);
    this._currentLocale = localeId;
  }

  private setDocumentI18n(localeId: LocaleId): void {
    this.document.documentElement.dir = getLangDir(localeId);
    this.document.documentElement.lang = localeId;
  }

  private getPluralTranslationUnit(
    value: number,
    pluralMap: PluralMap,
    locale?: string,
  ): TranslationUnit | undefined {
    const key = this.getPluralCategory(value, Object.keys(pluralMap), locale) as keyof PluralMap;
    const translationUnit = pluralMap[key];
    return translationUnit;
  }

  private getPluralCategory(value: number, cases: string[], locale?: string): string {
    let key = `=${value}`;

    if (cases.indexOf(key) > -1) {
      return key;
    }

    key = new NgLocaleLocalization(locale || this.currentLocale).getPluralCategory(value, locale);

    if (cases.indexOf(key) > -1) {
      return key;
    }

    if (cases.indexOf('other') > -1) {
      return 'other';
    }

    throw new Error(`No plural message found for value "${value}"`);
  }
}
